<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-12">
    <h3 class="cf-title cf-title--3 cf-text-align-left">
      Contact Info
    </h3>

    <cf-text-input
      v-model="contactInfo.fullName"
      type="text"
      label="Full Name"
      name="fullName"
      icon-class="fa-user-circle"
      :validations="{
        full_name: true,
        required: true
      }"
    />

    <cf-text-input
      v-if="emailIsShown"
      v-model="contactInfo.email"
      type="email"
      label="Email Address"
      name="email"
      :data-three-ds="paayEmailAttribute"
      :validations="{ required: emailIsRequired }"
      :show-as-optional="emailInputState === 'optional' && !requestBillingEmail"
    />

    <transition name="fade-down-only-in">
      <cf-phone-number-input
        v-if="phoneIsShown"
        v-model="contactInfo.phoneNumber"
        label="Phone Number"
        name="phoneNumber"
        :validations="{
          required: true
        }"
      />
    </transition>

    <!--
      Hidden inputs the Paay 3DS SDK scrapes for the cardholder's mobile
      phone. Paay reads the country calling code (mobileCC, e.g. "1" for US)
      and the national number (mobilePhoneNum) from separate DOM elements
      (https://docs.3dsintegrator.com/docs/dom-elements). cf-phone-number-input
      stores a single E.164 value, so these hidden mirrors split it back out.
    -->
    <template v-if="renderPaayMobileInputs">
      <cf-text-input
        v-show="false"
        type="text"
        label="mobileCC"
        name="mobileCC"
        :model-value="billingMobileCallingCode"
        data-three-ds="mobileCC"
      />

      <cf-text-input
        v-show="false"
        type="text"
        label="mobilePhoneNum"
        name="mobilePhoneNum"
        :model-value="billingMobileNationalNumber"
        data-three-ds="mobilePhoneNum"
      />
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import { parsePhoneNumberWithError } from 'libphonenumber-js';

  import parsePhoneFromApi from '@/helpers/parse-phone-from-api';

  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';

  export default {
    name: 'CfContactInformation',

    components: {
      CfPhoneNumberInput,
      CfTextInput
    },

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      emailInputState: {
        type: String,
        default: 'required',
        validator(value) {
          return ['required', 'optional', 'hidden'].includes(value);
        }
      },

      phoneIsRequired: {
        type: Boolean,
        default: false
      },

      tdsProvider: {
        type: String,
        default: ''
      },

      requestBillingEmail: {
        type: Boolean,
        default: false
      },

      requestBillingPhone: {
        type: Boolean,
        default: false
      },

      modelValue: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        contactInfo: {
          email: null,
          fullName: null,
          phoneNumber: null
        }
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser', 'session']),

      emailIsShown() {
        return this.emailInputState !== 'hidden' || this.requestBillingEmail;
      },

      emailIsRequired() {
        return this.emailInputState === 'required' || this.requestBillingEmail;
      },

      phoneIsShown() {
        return this.phoneIsRequired || this.session.smsReceiptOptIn || this.requestBillingPhone;
      },

      paayEmailAttribute() {
        return this.tdsProvider === 'Paay' && this.requestBillingEmail ? 'email' : null;
      },

      renderPaayMobileInputs() {
        return this.tdsProvider === 'Paay' && this.requestBillingPhone;
      },

      parsedBillingPhone() {
        if (!this.contactInfo.phoneNumber) return null;

        try {
          return parsePhoneNumberWithError(String(this.contactInfo.phoneNumber));
        }
        catch (_error) {
          return null;
        }
      },

      billingMobileCallingCode() {
        return this.parsedBillingPhone ? this.parsedBillingPhone.countryCallingCode : null;
      },

      billingMobileNationalNumber() {
        return this.parsedBillingPhone ? this.parsedBillingPhone.nationalNumber : null;
      }
    },

    watch: {
      contactInfo: {
        deep: true,
        handler: 'updateContactInfo',
        immediate: true
      },
      'currentUser.accountId': {
        handler: 'signedInHandler',
        immediate: true
      }
    },

    methods: {
      signedInHandler() {
        if (this.currentUser.accountId) {
          this.contactInfo.email = this.currentUser.email;
          this.contactInfo.fullName = this.currentUser.fullName;
          this.contactInfo.phoneNumber = parsePhoneFromApi(this.currentUser.primarySmsNumber);
        }
      },

      updateContactInfo() {
        this.$emit('update:modelValue', {
          ...this.contactInfo,
          fullName: this.contactInfo.fullName ? this.contactInfo.fullName.trim() : this.contactInfo.fullName
        });
      }
    }
  };
</script>
